import * as React from 'react';
import axios from 'axios';
import ACTIONS from '../actions';
import { API_BASE_URL } from '../../config';
import useNotification from '../notification/notificationContext';
import { NOTIFICATIONS } from '../../config';

export const TreatmentsContext = React.createContext();
const stripePublicKey =
  "pk_test_51KBzn2F4Ojf7qpxSZRvts1EkZNsUJuTEitxlcKuTRB8cippVmegztFDaLPNTj1TRPa0WtuuR2sKyOl5fYQaXqEfi00uuDiUzne";


function useTreatments() {
  const context = React.useContext(TreatmentsContext);

  if (!context) throw new Error('useTreatments must be used within a TreatmentsProvider');

  const [treatmentsState, dispatch] = context;

  const { setNotification } = useNotification();

  const token = localStorage.getItem('jwtToken');
  if (token) axios.defaults.headers.common['x-access-token'] = token; 

  const getAllTreatments = (source) => {
    return axios.get(`${API_BASE_URL}/treatment/get-all`, { 
      cancelToken: source.token
    }).then(res => {
      if (res.status === 200) {
        dispatch({
          type: ACTIONS.GET_ALL_TREATMENTS,
          payload: res.data.data
        });
        return res;
      }
      return;
    }).catch(err => {
      if (axios.isCancel(err)) return;
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: 'We were unable to retrieve the list of treatments'
      })
      throw err;
    })
  }

  const getTreatmentInfo = (treatmentId) => {
    return axios.get(`${API_BASE_URL}/treatment/get-one/${treatmentId}`, { 
    }).then(res => {
      return res.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: 'We were unable to retrieve the list of treatments'
      })
      throw err;
    })
  }

  const getPartnerTreatments = (source) => {
    return axios.get(`${API_BASE_URL}/treatment/partner`, { 
      cancelToken: source.token
    }).then(res => {
      if (res.status === 200) return res;
      return;
    }).catch(err => {
      if (axios.isCancel(err)) {
        return;
      }
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: 'We were unable to retrieve the list of treatments'
      })
      throw err;
    })
  }

  const getPatientTreatments = (source) => {
    return axios.get(`${API_BASE_URL}/treatment/patient`, { 
      cancelToken: source.token
    }).then(res => {
      if (res.status === 200) return res;
      return;
    }).catch(err => {
      if (axios.isCancel(err)) return;
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: 'We were unable to retrieve the list of treatments'
      })
      throw err;
    })
  }

  const createTreatment = (data) => {
    return axios.post(`${API_BASE_URL}/treatment/create`, data).then(res => {
      const response = res.data;
      dispatch({
        type: ACTIONS.CREATE_TREATMENT,
        payload: response.data
      });
      return response;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'An error occurred'
      });
      throw err;
    })
  }

  const partnerCreateTreatment = (data) => {
    return axios.post(`${API_BASE_URL}/treatment/partner-create`, data).then(res => {
      const response = res.data;
      dispatch({
        type: ACTIONS.CREATE_TREATMENT,
        payload: response.data
      });
      return response;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: "Server error",
        message: err.response?.data?.message || "An error occurred",
      });
      throw err;
    })
  }
  
  const createScanAndGo = (data) => {
    return axios.post(`${API_BASE_URL}/treatment/create-scan-and-go`, data).then(res => {
      const response = res.data;
      return response;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: "Server error",
        message: err.response?.data?.message || "An error occurred",
      });
      throw err;
    })
  }

  const getTreatmentCollaborators = (treatmentId) => {
    return axios.get(`${API_BASE_URL}/treatment-collaborator/get/${treatmentId}`).then(res => {
      dispatch({
        type: ACTIONS.GET_ALL_TREATMENT_COLLABORATORS,
        payload: res.data.data
      });
      return res.data.data
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: "Server error",
        message: err.response?.data?.message || "An error occurred",
      });
      throw err;
    })
  }

  const removeTreatmentCollaborator = ({ treatmentId, collaboratorId  }) => {
    axios.post(`${API_BASE_URL}/treatment-collaborator/remove/${treatmentId}/${collaboratorId}`).then(res => {
       dispatch({
        type: ACTIONS.REMOVE_TREATMENT_COLLABORATOR,
        payload: collaboratorId
      })
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'Collaborator removed successfully'
      });
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: "Server error",
        message: err.response?.data?.message || "An error occurred",
      });
      throw err;
    })
  }

  const addTreatmentCollaborator = ({ treatmentId, collaboratorId }) => {
    axios.post(`${API_BASE_URL}/treatment-collaborator/add/${treatmentId}/${collaboratorId}`).then(res => {
      dispatch({
        type: ACTIONS.ADD_TREATMENT_COLLABORATOR,
        payload: res.data?.data
      })
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'Collaborator added successfully'
      });
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: "Server error",
        message: err.response?.data?.message || "An error occurred",
      });
      throw err;
    })
  };

  const getTreatmentDocuments = (treatmentId) => {
    axios.get(`${API_BASE_URL}/file/treatment/${treatmentId}`).then(res => {
      dispatch({
        type: ACTIONS.GET_TREATMENT_DOCUMENTS,
        payload: res.data.data
      });
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to retrieve treatment documents'
      });
      throw err;
    })
  };

  const getConversationMessages = async (conversationId) => {
    try{
      const response = await axios.get(`${API_BASE_URL}/message/get-all-messages/${conversationId}`);
      dispatch({
        type: ACTIONS.GET_TREATMENT_MESSAGES,
        payload: response.data.data
      });
      return response;
    } catch (err) {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to retrieve treatment messages'
      });
      throw err;
    }
  };

  const getTreatmentConversations = async (treatmentId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/conversation/treatment-conversations/${treatmentId}`
      );
      dispatch({
        type: ACTIONS.TREATMENT_CONVERSATION,
        payload: response.data.data
      })
      return response.data.data;
    } catch (err) {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: "Server error",
        message: err.response?.data?.message || "Error in getting treatment conversation",
      });
      throw err;
    }
  }

  const sendMessage = async({messageText, conversationId}) => {
    const data = {
      message_text: messageText
    };
    return axios.post(`${API_BASE_URL}/message/send-message/${conversationId}`, data).then(response => {
      return response.data.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to get messages'
      })
      throw err;
    });
  }

  const deleteTreatmentDocument = (treatmentId, fileName) => {
    axios.delete(`${API_BASE_URL}/file/treatment/${treatmentId}/${fileName}`).then(res => {
      dispatch({
        type: ACTIONS.DELETE_TREATMENT_DOCUMENT,
        payload: fileName
      });
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'File deleted successfully.'
      });
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to delete file'
      })
      throw err;
    })
  };

  const downloadTreatmentDocument = (treatmentId, fileName) => {
    axios.get(`${API_BASE_URL}/file/treatment/${treatmentId}/${fileName}`).then(res => {
      const downloadUrl = res.data.data;
      const newWindow = window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      if (newWindow) newWindow.opener = null;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to download file'
      })
      throw err;
    })
  };

  const uploadTreatmentDocument = (treatmentId, file, patientViewable) => {
    var formData = new FormData();
    formData.append("treatmentFile", file);
    formData.append("patientViewable", patientViewable);
    axios.post(`${API_BASE_URL}/file/treatment/${treatmentId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
    }).then(res => {
      let uploadedFile = res.data.data;
      uploadedFile['patientViewable'] = patientViewable;
      dispatch({
        type: ACTIONS.UPLOAD_TREATMENT_DOCUMENT,
        payload: uploadedFile
      });
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Upload success',
        message: 'File was successfully uploaded'
      })
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to upload document'
      })
      throw err;
    })
  };

  const createTreatmentTransaction = async (treatment_id, data) => {
    return axios.post(`${API_BASE_URL}/treatment-transaction/add/${treatment_id}`, data).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: "Success",
        message: "Transaction added successfully",
      });
      return res.data
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: "Server error",
        message: err.response?.data?.message || "We were unable to add treatment transaction",
      });
      throw err;
    });
  }

  const getTreatmentTransactions = async (treatment_id) => {
    return axios.get(`${API_BASE_URL}/treatment-transaction/get/${treatment_id}`).then(res => {
      dispatch({
        type: ACTIONS.GET_ALL_TREATMENT_TRANSACTIONS,
        payload: res.data.data
      })
      return res.data.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: "Server error",
        message: err.response?.data?.message || "We were unable to get treatment transactions",
      });
      throw err;
    });  
  }

  const getUnpaidUserTransactions = async (source) => {
    return axios.get(`${API_BASE_URL}/treatment-transaction/me/unpaid/`, { cancelToken: source.token }).then(res => {
      return res.data;
    }).catch(err => {
      if (axios.isCancel(err)) {
        return;
      } else {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: "Server error",
        message: err.response?.data?.message || "We were unable to get list of unpaid transactions",
      });
      throw err;
    }});  
  }
  

  const removeTreatmentTransaction = async (treatment_id, transaction_id) => {
    return axios.delete(`${API_BASE_URL}/treatment-transaction/remove/${treatment_id}/transaction/${transaction_id}`).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: "Successfully removed",
        message: 'Transaction was successfully removed'
      });
      return res.data
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: "Server error",
        message: err.response?.data?.message|| "We were unable to remove treatment transaction",
      });
      throw err;
    }) ;
  }

  const payForTreatmentTransaction = async (treatment_id, data) => {
    return axios.post(`${API_BASE_URL}/treatment-transaction/pay/${treatment_id}`,data).then(res => {
      return res.data;
    }).catch(err => {
      throw err;
    });
  };

  function getQueryString(data = {}) {
    return Object.entries(data)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      )
      .join("&");
  }

  const generateStripePaymentMethod = async (data) => {
    try {
      const response = await axios.post(
        `https://api.stripe.com/v1/payment_methods`,
        data,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${stripePublicKey}`,
          },
          withCredentials: true,
          transformRequest: getQueryString,
        }
      );
      return response;
    } catch (err) {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: "Payment error",
        message: err.response.data?.message || 'An error occurred',
      });
      throw err;
    }
  };

  const deleteTreatment = (treatmentId) => {
    return axios.delete(`${API_BASE_URL}/treatment/delete/${treatmentId}`).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'Treatment deleted successfully.'
      });
      return res;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to delete treatment'
      })
      throw err;
    })
  };

  const undoDeleteTreatment = (treatmentId) => {
    return axios.delete(`${API_BASE_URL}/treatment/undo-delete/${treatmentId}`).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'Treatment activated successfully.'
      });
      return res;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to delete treatment'
      })
      throw err;
    })
  };

  const forceDeleteTreatment = (treatmentId) => {
    return axios.delete(`${API_BASE_URL}/treatment/force-delete/${treatmentId}`).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'Treatment successfully erased from database.'
      });
      return res;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to delete treatment'
      })
      throw err;
    })
  };

  
  
  return {
    treatmentsState,
    dispatch,
    getAllTreatments,
    getTreatmentInfo,
    getPartnerTreatments,
    createTreatment,
    partnerCreateTreatment,
    createScanAndGo,
    addTreatmentCollaborator,
    getTreatmentCollaborators,
    removeTreatmentCollaborator,
    getConversationMessages,
    sendMessage,
    getTreatmentConversations,
    getTreatmentDocuments,
    uploadTreatmentDocument,
    deleteTreatmentDocument,
    downloadTreatmentDocument,
    createTreatmentTransaction,
    getTreatmentTransactions,
    removeTreatmentTransaction,
    payForTreatmentTransaction,
    generateStripePaymentMethod,
    deleteTreatment,
    forceDeleteTreatment,
    undoDeleteTreatment,
    getPatientTreatments,
    getUnpaidUserTransactions
  }
}

export default useTreatments;